import { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * The anchor hero is a fixed layer behind the page, so the scrolling panel has
 * to be pushed down until its top meets the hero's bottom edge. That offset
 * used to be a hardcoded `mt-[55vh]`/`mt-[60vh]` — identical on a 4K monitor
 * and a phone — so long hero copy on a narrow screen grew past the reserved
 * band and was painted behind the opaque panel, cut in a straight line, from
 * the very first frame.
 *
 * Rules encoded here:
 * 1. The panel already starts below the site header, so the margin needed is
 *    the hero's height minus wherever the panel naturally sits in the flow.
 *    We measure that offset rather than guessing it.
 * 2. Before measurement a fallback of `margin-top: 60svh` applies — the hero's
 *    own minimum height, in the same unit, so the two can never disagree on
 *    mobile browsers. Never smaller: too large leaves a gap (survivable), too
 *    small clips the copy (not). It is an INLINE STYLE, not a Tailwind class:
 *    a generated class name is invisible to Tailwind's source scanner, so the
 *    rule could silently vanish from the bundle and leave no margin at all.
 * 3. During prerendering (headless browser) no measured pixel value is
 *    emitted, so the saved HTML carries the CSS fallback rather than a pixel
 *    number baked at whatever viewport the snapshot used.
 */
const HERO_MIN_CSS = "60svh";

function isPrerender() {
  return typeof navigator !== "undefined" && (navigator as any).webdriver === true;
}

export function useHeroOffset() {
  const panelRef = useRef<HTMLDivElement>(null);
  const [heroHeight, setHeroHeight] = useState<number | null>(null);
  const [margin, setMargin] = useState<number | null>(null);

  const onHeroHeight = useCallback((h: number) => setHeroHeight(h), []);

  useLayoutEffect(() => {
    if (heroHeight === null || isPrerender()) return;
    const el = panelRef.current;
    if (!el) return;
    const compute = () => {
      const currentMargin = parseFloat(getComputedStyle(el).marginTop) || 0;
      // Where the panel would sit with no margin at all (i.e. under the header).
      const flowTop = el.getBoundingClientRect().top + window.scrollY - currentMargin;
      setMargin(Math.max(0, Math.round(heroHeight - flowTop)));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [heroHeight]);

  const usePx = margin !== null && !isPrerender();

  return {
    panelRef,
    onHeroHeight,
    offsetStyle: { marginTop: usePx ? `${margin}px` : HERO_MIN_CSS },
    offsetClass: "",
  };
}
